import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, Send, Ban, RotateCcw, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inp = "h-10 w-full rounded-lg border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500";
const act = "inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3 py-1.5 text-xs font-semibold transition-colors hover:border-black";

const copy = (link) => navigator.clipboard.writeText(link).then(() => toast.success("Link d'invito copiato"));

const InviteForm = ({ onInvited }) => {
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staff", f);
      toast.success(`Invito inviato a ${data.email}`);
      setF({ name: "", email: "" }); onInvited(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid="staff-invite-form">
      <label className="space-y-1"><span className="text-xs text-stone-600">Nome</span><input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="staff-invite-name" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-600">Email</span><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="staff-invite-email" /></label>
      <button disabled={busy} className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60" data-testid="staff-invite-submit">
        <UserPlus className="h-4 w-4" />{busy ? "Invio..." : "Invita"}
      </button>
    </form>
  );
};

const Status = ({ s }) => {
  const [label, cls] = s.suspended ? ["Sospeso", "bg-rose-100 text-rose-700"] : s.invite_pending ? ["Invito in attesa", "bg-amber-100 text-amber-800"] : ["Attivo", "bg-emerald-100 text-emerald-700"];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={`staff-status-${s.user_id}`}>{label}</span>;
};

const StaffRow = ({ s, link, onChange, onLink, onRemoved }) => {
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); } catch (err) { toast.error(formatApiError(err)); } };
  const resend = () => run(async () => onLink(s.user_id, (await api.post(`/admin/staff/${s.user_id}/resend`)).data.invite_link), "Nuovo invito inviato");
  const suspend = () => {
    if (!s.suspended && !window.confirm(`Sospendere ${s.name}? Perderà subito l'accesso al pannello.`)) return;
    run(async () => onChange((await api.post(`/admin/staff/${s.user_id}/suspend`, { suspended: !s.suspended })).data), s.suspended ? "Accesso riattivato" : "Accesso sospeso");
  };
  const remove = () => window.confirm(`Rimuovere definitivamente ${s.name}?`) && run(async () => { await api.delete(`/admin/staff/${s.user_id}`); onRemoved(s.user_id); }, "Amministratore rimosso");
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`staff-row-${s.user_id}`}>
      <div><p className="font-semibold">{s.name} <Status s={s} /></p><p className="text-sm text-stone-500">{s.email}</p></div>
      <div className="flex flex-wrap gap-2">
        {s.invite_pending && <button onClick={resend} className={act} data-testid={`staff-resend-${s.user_id}`}><Send className="h-3.5 w-3.5" />Reinvia invito</button>}
        {s.invite_pending && link && <button onClick={() => copy(link)} className={act} data-testid={`staff-copy-link-${s.user_id}`}><Copy className="h-3.5 w-3.5" />Copia link</button>}
        <button onClick={suspend} className={act} data-testid={`staff-suspend-${s.user_id}`}>{s.suspended ? <><RotateCcw className="h-3.5 w-3.5" />Riattiva</> : <><Ban className="h-3.5 w-3.5" />Sospendi</>}</button>
        <button onClick={remove} className={`${act} text-rose-600 hover:border-rose-600`} data-testid={`staff-remove-${s.user_id}`}><Trash2 className="h-3.5 w-3.5" />Rimuovi</button>
      </div>
    </li>
  );
};

export const AdminStaff = () => {
  const [rows, setRows] = useState([]);
  const [links, setLinks] = useState({});
  useEffect(() => { api.get("/admin/staff").then((r) => setRows(r.data)); }, []);
  const setLink = (id, l) => setLinks((m) => ({ ...m, [id]: l }));
  return (
    <div className="space-y-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="admin-staff">
      <div>
        <h2 className="font-display text-lg font-bold">Amministratori staff</h2>
        <p className="text-sm text-stone-600">Invita un collaboratore: riceve un'email (link valido 48 ore) e sceglie la sua password. Può usare tutto il pannello tranne questa sezione.</p>
      </div>
      <InviteForm onInvited={(d) => { setRows((r) => [...r, d]); setLink(d.user_id, d.invite_link); }} />
      {rows.length === 0 ? <p className="text-sm text-stone-500" data-testid="staff-empty">Nessun amministratore staff.</p> : (
        <ul className="divide-y divide-black/10" data-testid="staff-list">
          {rows.map((s) => <StaffRow key={s.user_id} s={s} link={links[s.user_id]} onLink={setLink}
            onChange={(d) => setRows((r) => r.map((x) => (x.user_id === d.user_id ? d : x)))} onRemoved={(id) => setRows((r) => r.filter((x) => x.user_id !== id))} />)}
        </ul>
      )}
    </div>
  );
};
